<template>
  <div>
    <div class="px-4">
      <div class="body p-4">
        <div class="d-flex align-items-center gap-3 mb-1">
          <button
            @click="$router.push('/cliente/nfse/servicos')"
            class="btn font-weight-bold btn-primary text-white btn-shadow"
            v-prevent-click
          >
            <i class="fa fa-arrow-left me-2"></i>Servi&ccedil;os
          </button>
          <button
            type="button"
            class="btn btn-outline-secondary"
            @click="$router.push(`/cliente/nfse/servicos/editar/${serviceId}`)"
          >
            <i class="fas fa-edit me-2"></i>Editar servi&ccedil;o
          </button>
        </div>

        <h4 class="text-gray mt-3" v-if="serviceName">{{ serviceName }}</h4>

        <span class="text-primary h2 d-flex align-items-center mt-2">
          <span class="text-gray">Perfis municipais</span>
        </span>

        <div class="text-end mt-4">
          <button
            type="button"
            class="px-3 btn text-uppercase font-weight-light btn-secondary text-white btn-shadow mb-2 btn-sm"
            @click="openAddForm"
            v-prevent-click
          >
            <i class="fas fa-plus me-2"></i>Adicionar perfil municipal
          </button>
        </div>

        <div class="d-flex justify-content-center mt-4" v-if="loaderActive && !profiles.length">
          <div class="spinner-border text-primary spinner-border" role="status">
            <span class="visually-hidden">Aguarde...</span>
          </div>
        </div>

        <div class="card p-3 mt-4 text-start" v-else-if="!profiles.length && !showForm">
          Nenhum perfil municipal cadastrado.
        </div>

        <div class="card p-3 mt-4 text-start" v-else-if="profiles.length">
          <div class="table-responsive">
            <table class="table table-hover align-middle mb-0">
              <thead>
                <tr>
                  <th>Munic&iacute;pio (IBGE)</th>
                  <th>cTribMun</th>
                  <th>Descri&ccedil;&atilde;o municipal</th>
                  <th>Al&iacute;quota ISS</th>
                  <th>Ativo</th>
                  <th class="text-center">A&ccedil;&otilde;es</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="profile in profiles" :key="profile.id">
                  <td>{{ profile.city_ibge_code || "-" }}</td>
                  <td>{{ profile.ctrib_mun || "-" }}</td>
                  <td>{{ profile.municipal_description || "-" }}</td>
                  <td>{{ profile.iss_rate != null ? profile.iss_rate + "%" : "-" }}</td>
                  <td>
                    <span
                      class="badge"
                      :class="profile.is_active ? 'bg-success' : 'bg-secondary'"
                    >{{ profile.is_active ? "Sim" : "Não" }}</span>
                  </td>
                  <td>
                    <div class="d-flex justify-content-center">
                      <button
                        class="btn btn-light shadow-sm me-2"
                        type="button"
                        v-tooltip="'Editar'"
                        @click="editProfile(profile)"
                      >
                        <i class="fas fa-edit text-primary"></i>
                      </button>
                      <button
                        class="btn btn-light shadow-sm"
                        type="button"
                        v-tooltip="'Remover'"
                        @click="deleteProfile(profile)"
                      >
                        <i class="fas fa-trash text-danger"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="card p-4 mt-4" v-if="showForm">
          <h6 class="text-primary mb-3">
            {{ editingProfile ? "Editar perfil municipal" : "Novo perfil municipal" }}
          </h6>
          <form @submit.prevent="submitProfileForm">
            <div class="row text-start">

              <div class="col-lg-4 col-md-6 col-sm-12 text-primary mb-4">
                <label class="form-label mb-2">
                  Munic&iacute;pio (c&oacute;digo IBGE)*
                </label>
                <v-select
                  v-model="selectedMunicipio"
                  :options="filteredMunicipios"
                  :get-option-label="(m) => `${m.nome} (${m.uf})`"
                  :filterable="false"
                  @search="onMunicipioSearch"
                  placeholder="Digite o nome da cidade"
                  no-drop-on-empty
                >
                  <template #no-options="{ search }">
                    <span v-if="!search || search.length < 2">
                      Digite o nome da cidade para pesquisar
                    </span>
                    <span v-else>Nenhuma cidade encontrada</span>
                  </template>
                </v-select>
                <small class="text-muted" v-if="form.city_ibge_code">IBGE: {{ form.city_ibge_code }}</small>
              </div>

              <div class="col-12 text-primary mb-3" v-if="municipalCodes.length">
                <label class="form-label mb-2">
                  Selecionar do cat&aacute;logo (opcional)
                </label>
                <select class="form-select" @change="onCatalogSelect($event)">
                  <option value="">— Digitar manualmente —</option>
                  <option
                    v-for="code in municipalCodes"
                    :key="code.id"
                    :value="code.id"
                    :data-code="code.code"
                    :data-description="code.description"
                  >
                    {{ code.code }} — {{ code.description }}
                  </option>
                </select>
                <div class="text-center py-1" v-if="municipalCodesLoading">
                  <span class="spinner-border spinner-border-sm text-primary"></span>
                </div>
              </div>

              <div class="col-lg-4 col-md-6 col-sm-12 text-primary mb-4">
                <label for="input-ctrib-mun" class="form-label mb-2">
                  C&oacute;digo municipal (cTribMun)*
                </label>
                <input
                  type="text"
                  id="input-ctrib-mun"
                  class="form-control"
                  placeholder="01400"
                  v-model="form.ctrib_mun"
                  @input="form.municipal_service_code_id = null"
                />
              </div>

              <div class="col-lg-4 col-md-6 col-sm-12 text-primary mb-4">
                <label for="input-municipal-description" class="form-label mb-2">
                  Descri&ccedil;&atilde;o municipal
                </label>
                <input
                  type="text"
                  id="input-municipal-description"
                  class="form-control"
                  placeholder="Serviços de programação de computadores"
                  v-model="form.municipal_description"
                />
              </div>

              <div class="col-lg-4 col-md-6 col-sm-12 text-primary mb-4">
                <label for="input-iss-rate" class="form-label mb-2">
                  Al&iacute;quota ISS padr&atilde;o (%)
                </label>
                <input
                  type="number"
                  id="input-iss-rate"
                  class="form-control"
                  placeholder="2.00"
                  step="0.01"
                  min="0"
                  max="100"
                  v-model="form.iss_rate"
                />
              </div>

              <div class="col-12 text-primary mb-4">
                <label for="input-notes" class="form-label mb-2">
                  Observa&ccedil;&otilde;es
                </label>
                <textarea
                  id="input-notes"
                  class="form-control"
                  rows="2"
                  v-model="form.notes"
                ></textarea>
              </div>
            </div>

            <div class="d-flex gap-2">
              <button
                type="submit"
                class="btn btn-primary text-white btn-shadow"
                :disabled="loaderActive"
              >
                <span v-if="!loaderActive">Salvar</span>
                <span v-else>Aguarde...</span>
              </button>
              <button
                type="button"
                class="btn btn-outline-secondary"
                @click="cancelForm"
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>

      </div>
    </div>
  </div>
</template>
<script>
import "vue-select/dist/vue-select.css";
import municipios from "@/data/nfse/municipios.json";
import api from "@/services/api";
import { getErrorMessage, searchMunicipios } from "@/utils/nfseHelpers";
import { useToast } from "vue-toastification";

const toast = useToast();

export default {
  name: "nfse-service-municipal-profiles",
  data() {
    return {
      serviceId: null,
      serviceName: "",
      profiles: [],
      loaderActive: false,
      showForm: false,
      editingProfile: null,
      form: {
        city_ibge_code: "",
        ctrib_mun: "",
        municipal_service_code_id: null,
        municipal_description: "",
        iss_rate: "",
        notes: "",
      },
      selectedMunicipio: null,
      municipioSearch: "",
      municipalCodes: [],
      municipalCodesLoading: false,
    };
  },
  computed: {
    filteredMunicipios() {
      return searchMunicipios(municipios, this.municipioSearch);
    },
  },
  watch: {
    selectedMunicipio(m) {
      if (!m) {
        this.form.city_ibge_code = "";
        this.municipalCodes = [];
        return;
      }
      this.form.city_ibge_code = m.codigo;
      this.municipalCodes = [];
      this.loadMunicipalCodes(m.codigo);
    },
  },
  async mounted() {
    this.serviceId = this.$route.params.serviceId;
    await Promise.all([this.loadService(), this.loadProfiles()]);
  },
  methods: {
    async loadService() {
      try {
        const { data } = await api.get(`/company/nfse/services/${this.serviceId}`);
        const service = data.data || data;
        this.serviceName = service.name || "";
      } catch {
        // non-critical — header can show empty
      }
    },
    async loadProfiles() {
      try {
        this.loaderActive = true;
        const { data } = await api.get(
          `/company/nfse/services/${this.serviceId}/municipal-profiles`
        );
        this.profiles = Array.isArray(data) ? data : (data.data || []);
      } catch (e) {
        toast.error(
          getErrorMessage(e, "Ocorreu um erro ao carregar os perfis municipais.")
        );
      } finally {
        this.loaderActive = false;
      }
    },
    async loadMunicipalCodes(ibge) {
      if (!ibge || ibge.length !== 7) return;
      this.municipalCodesLoading = true;
      try {
        const { data } = await api.get("/company/nfse/municipal-service-codes", {
          params: { city_ibge_code: ibge },
        });
        this.municipalCodes = data.data || data || [];
      } catch {
        this.municipalCodes = [];
      } finally {
        this.municipalCodesLoading = false;
      }
    },
    onMunicipioSearch(search) {
      this.municipioSearch = search;
    },
    onCatalogSelect(event) {
      const selected = event.target;
      const option = selected.options[selected.selectedIndex];
      if (!option.value) return;
      const code = option.getAttribute("data-code");
      const description = option.getAttribute("data-description");
      const id = option.value;
      this.form.municipal_service_code_id = id;
      this.form.ctrib_mun = code;
      if (!this.form.municipal_description) {
        this.form.municipal_description = description;
      }
    },
    openAddForm() {
      this.editingProfile = null;
      this.selectedMunicipio = null;
      this.municipioSearch = "";
      this.form = {
        city_ibge_code: "",
        ctrib_mun: "",
        municipal_service_code_id: null,
        municipal_description: "",
        iss_rate: "",
        notes: "",
      };
      this.municipalCodes = [];
      this.showForm = true;
    },
    editProfile(profile) {
      this.editingProfile = profile;
      this.municipioSearch = "";
      this.selectedMunicipio =
        municipios.find((m) => m.codigo === String(profile.city_ibge_code)) || null;
      this.form = {
        city_ibge_code: profile.city_ibge_code || "",
        ctrib_mun: profile.ctrib_mun || "",
        municipal_service_code_id: profile.municipal_service_code_id || null,
        municipal_description: profile.municipal_description || "",
        iss_rate: profile.iss_rate ?? "",
        notes: profile.notes || "",
      };
      this.municipalCodes = [];
      this.loadMunicipalCodes(profile.city_ibge_code);
      this.showForm = true;
    },
    cancelForm() {
      this.showForm = false;
      this.editingProfile = null;
      this.selectedMunicipio = null;
      this.municipioSearch = "";
      this.municipalCodes = [];
    },
    buildProfilePayload() {
      const p = {
        city_ibge_code: String(
          this.selectedMunicipio?.codigo ?? this.form.city_ibge_code
        ).trim(),
        ctrib_mun: String(this.form.ctrib_mun).trim(),
      };
      if (this.form.municipal_service_code_id) {
        p.municipal_service_code_id = this.form.municipal_service_code_id;
      }
      const desc = String(this.form.municipal_description || "").trim();
      if (desc) p.municipal_description = desc;
      if (this.form.iss_rate !== "" && this.form.iss_rate !== null) {
        p.iss_rate = Number(this.form.iss_rate);
      }
      const notes = String(this.form.notes || "").trim();
      if (notes) p.notes = notes;
      return p;
    },
    validateProfileForm() {
      if (!this.form.city_ibge_code || this.form.city_ibge_code.length !== 7) {
        toast.error("Informe o código IBGE do município (7 dígitos).");
        return false;
      }
      if (!this.form.ctrib_mun) {
        toast.error("Informe o código de tributação municipal.");
        return false;
      }
      return true;
    },
    async submitProfileForm() {
      if (!this.validateProfileForm()) return;
      const payload = this.buildProfilePayload();

      try {
        this.loaderActive = true;
        if (this.editingProfile) {
          await api.put(
            `/company/nfse/services/${this.serviceId}/municipal-profiles/${this.editingProfile.id}`,
            payload
          );
          toast.success("Perfil municipal atualizado com sucesso.");
        } else {
          await api.post(
            `/company/nfse/services/${this.serviceId}/municipal-profiles`,
            payload
          );
          toast.success("Perfil municipal cadastrado com sucesso.");
        }
        this.cancelForm();
        await this.loadProfiles();
      } catch (e) {
        toast.error(
          getErrorMessage(e, "Ocorreu um erro ao salvar o perfil municipal.")
        );
      } finally {
        this.loaderActive = false;
      }
    },
    async deleteProfile(profile) {
      if (!confirm(`Remover perfil do município ${profile.city_ibge_code}?`)) return;
      try {
        this.loaderActive = true;
        await api.delete(
          `/company/nfse/services/${this.serviceId}/municipal-profiles/${profile.id}`
        );
        toast.success("Perfil municipal removido com sucesso.");
        await this.loadProfiles();
      } catch (e) {
        toast.error(
          getErrorMessage(e, "Ocorreu um erro ao remover o perfil municipal.")
        );
      } finally {
        this.loaderActive = false;
      }
    },
  },
};
</script>
